import React, { useMemo, useState } from 'react';
import { Table, Button, Space, Typography, Input, Tag, Popconfirm, Select, Upload, message } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import { CreateCustomerFamilyData } from '../../services/customers';
import type { UploadFile } from 'antd';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

interface FamilyTabProps {
  isEdit: boolean;
  isEditing?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  isCustomerPortal?: boolean;
  spouses: CreateCustomerFamilyData[];
  children: CreateCustomerFamilyData[];
  deletedFamilyMembers?: Array<{ type: 'spouse' | 'children'; index: number; data: any }>;
  changeRequests?: Array<{ field_name: string; status: string; old_value?: string; new_value?: string; id: number }>;
  addSpouse: () => void;
  removeSpouse: (index: number) => void;
  addChild: () => void;
  removeChild: (index: number) => void;
  getFamilyDisplayValue: (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no') => string;
  handleFamilyFieldChange: (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no', value: string) => void;
  handleApproveChangeRequest?: (requestId: number) => void;
  handleRejectChangeRequest?: (requestId: number) => void;
  handleApproveFamilyDelete?: (requestId: number) => void;
  handleRejectFamilyDelete?: (requestId: number) => void;
  handleFamilyTypeChange: (type: 'spouse' | 'children', index: number, newType: 'spouse' | 'children') => void;
  customerId?: number;
  onFamilyFileChange?: (type: 'spouse' | 'children', index: number, file: UploadFile | null) => void;
  familyFiles?: Record<string, UploadFile[]>;
}

export const FamilyTab: React.FC<FamilyTabProps> = ({
  isEdit,
  isEditing = false,
  canEdit: canEditPermission = false,
  canDelete: canDeletePermission = false,
  isCustomerPortal = false,
  spouses,
  children,
  deletedFamilyMembers = [],
  changeRequests = [],
  addSpouse,
  removeSpouse,
  addChild,
  removeChild,
  getFamilyDisplayValue,
  handleFamilyFieldChange,
  handleApproveChangeRequest,
  handleRejectChangeRequest,
  handleApproveFamilyDelete,
  handleRejectFamilyDelete,
  handleFamilyTypeChange,
  customerId,
  onFamilyFileChange,
  familyFiles = {},
}) => {
  const [fileActionLoading, setFileActionLoading] = useState<Record<number, { view?: boolean }>>({});

  const setFileLoading = (familyId: number, action: 'view', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [familyId]: {
        ...prev[familyId],
        [action]: isLoading,
      },
    }));
  };

  const handleView = async (familyId: number) => {
    if (!familyId) return;
    if (!isCustomerPortal && !customerId) return;
    
    const previewWindow = window.open('', '_blank');

    try {
      setFileLoading(familyId, 'view', true);
      const isCustomerPortalPath = isCustomerPortal;
      let url: string;
      let apiClient: any;
      
      if (isCustomerPortalPath) {
        url = `/customer/family/${familyId}/attachment/view`;
        apiClient = customerApi;
      } else {
        url = `/customers/${customerId}/family/${familyId}/attachment/view`;
        apiClient = api;
      }
      
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const blobUrl = window.URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank');
      }
      
      if (previewWindow) {
        previewWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(blobUrl);
        });
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error: any) {
      message.error('Failed to view file');
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    } finally {
      setFileLoading(familyId, 'view', false);
    }
  };

  const handleViewRequestedAttachment = async (type: 'spouse' | 'children', index: number) => {
    if (!customerId) return;
    try {
      const response = await api.get(`/customer-change-requests/${customerId}/family/${type}/${index}/attachment/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      message.error('Failed to view requested attachment');
    }
  };
  
  // In create mode (!isEdit), always allow editing. In edit mode, use isEditing state.
  const canEdit = (!isEdit || isEditing) && canEditPermission;
  const canDelete = (!isEdit || isEditing) && canEditPermission;
  const maxFamilyCount = 10;

  // Get change request for a specific field
  const getFieldChangeRequest = (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no', memberId?: number) => {
    return changeRequests.find(cr => {
      if (cr.status !== 'pending') return false;
      // Match pattern: family_{type}_{index}_{field}
      const pattern = `family_${type}_${index}_${field}`;
      return cr.field_name === pattern;
    });
  };

  // Get delete change requests
  const getDeleteChangeRequest = (type: 'spouse' | 'children', index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `family_${type}_${index}_delete` && cr.status === 'pending'
    );
  };

  // Check if a family member is marked for deletion
  const isMarkedForDeletion = (type: 'spouse' | 'children', index: number) => {
    return deletedFamilyMembers.some(d => d.type === type && d.index === index) ||
           getDeleteChangeRequest(type, index) !== undefined;
  };

  // Process family members with change requests (similar to AddressTab)
  const processedFamilyMembers = useMemo(() => {
    // Create a set to track which members (by id) are deleted for more accurate matching
    const deletedMemberIds = new Set<number>();
    deletedFamilyMembers.forEach(deleted => {
      if (deleted.data?.id) {
        deletedMemberIds.add(deleted.data.id);
      }
    });
    
    // Start with all current members, calculating their original indices correctly
    // Process spouses and children separately, but combine them for display
    const allMembers: Array<any> = [];
    
    // First, add all current spouses with their calculated original indices
    spouses.forEach((spouse, currentIdx) => {
      const spouseId = (spouse as any).id;
      // Skip if this spouse is marked for deletion
      if (!spouseId || !deletedMemberIds.has(spouseId)) {
        // Calculate original index: current index + number of deleted items before this position
        let originalIdx = currentIdx;
  deletedFamilyMembers.forEach(deleted => {
          if (deleted.type === 'spouse' && deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        // Save both original index (_crIndex) and actual index in spouses array (_actualIndex)
        allMembers.push({ ...spouse, _type: 'spouse', _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });
    
    // Then, add all current children with their calculated original indices
    children.forEach((child, currentIdx) => {
      const childId = (child as any).id;
      // Skip if this child is marked for deletion
      if (!childId || !deletedMemberIds.has(childId)) {
        // Calculate original index: current index + number of deleted items before this position
        let originalIdx = currentIdx;
        deletedFamilyMembers.forEach(deleted => {
          if (deleted.type === 'children' && deleted.index <= currentIdx) {
            originalIdx++;
          }
        });
        // Save both original index (_crIndex) and actual index in children array (_actualIndex)
        allMembers.push({ ...child, _type: 'children', _crIndex: originalIdx, _actualIndex: currentIdx });
      }
    });

    // Sort by _crIndex to ensure correct order before inserting deleted items
    allMembers.sort((a, b) => a._crIndex - b._crIndex);

    // Add deleted members back at their original positions using splice
    // Process deleted items in reverse order of index to avoid index shifting issues
    const sortedDeleted = [...deletedFamilyMembers].sort((a, b) => b.index - a.index);
    sortedDeleted.forEach(deleted => {
      if (deleted.data && deleted.data.id) {
        // Check if this member is already in allMembers
        const exists = allMembers.some(m => {
          const mId = (m as any).id;
          return mId && mId === deleted.data.id;
        });
        if (!exists) {
          // Insert at the original index position using splice
          allMembers.splice(deleted.index, 0, { 
            ...deleted.data, 
            _crIndex: deleted.index, 
            _isDeletedLocal: true,
            _type: deleted.type,
          });
        }
    }
  });

  // Add new members from change requests (pending add requests)
    // Handle family_{type}_{index}_add format: each request contains a single member object (not an array)
    let addRequestIndex = 0;
  changeRequests.forEach(cr => {
    if (cr.status === 'pending' && cr.field_name.endsWith('_add')) {
      const parts = cr.field_name.split('_');
        if (parts.length >= 3 && parts[0] === 'family') {
        const type = parts[1] as 'spouse' | 'children';
        const index = parseInt(parts[2], 10);
        try {
          const newMember = JSON.parse(cr.new_value || '{}');
            
            // Skip if empty
            if (!newMember.name || !newMember.name.trim()) {
              return;
            }
            
            // Check if this member already exists in allMembers (already processed)
            const existsInAllMembers = allMembers.some(m => {
              const mId = (m as any).id;
              const newMemberId = newMember.id || '';
              if (mId && newMemberId && mId === newMemberId) {
                return true;
              }
              // If no id, compare by type+name+identification_no
              if (!mId && !newMemberId) {
                return m._type === type && 
                       m.name === newMember.name && 
                       m.identification_no === newMember.identification_no;
              }
              return false;
            });
            
            // Only add if it doesn't exist in allMembers (to avoid duplicates)
            if (!existsInAllMembers) {
              // Use a high index to ensure these appear at the end
              const placeholder = { 
                ...newMember, 
                _isNewFromRequest: true, 
                _addRequest: cr, 
                _crIndex: 999 + addRequestIndex,
                _type: type,
              };
              // Always append add-request rows to the end
              allMembers.push(placeholder as any);
              addRequestIndex++;
            }
          } catch (e) {
            console.error('FamilyTab: Error parsing family_add request:', e);
          }
        }
      }
    });

    const memberData = allMembers.map((member, idx) => {
      const crIdx = member._crIndex !== undefined ? member._crIndex : idx;
      const type = member._type || 'spouse';
      // Check if this specific member (by id) is marked for deletion
      const memberId = (member as any).id;
      const isDeleted = member._isDeletedLocal || 
                       (memberId && deletedFamilyMembers.some(d => {
                         if (d.type === type && d.index === crIdx && d.data) {
                           return d.data.id === memberId;
            }
                         return false;
                       })) ||
                       getDeleteChangeRequest(type, crIdx) !== undefined;
      const deleteReq = getDeleteChangeRequest(type, crIdx);
      return {
        ...member,
        key: `family-${type}-${crIdx}-${idx}-${memberId || 'new'}-${member._isNewFromRequest ? 'new-request' : ''}`,
        originalIndex: crIdx,
        actualIndex: member._actualIndex !== undefined ? member._actualIndex : undefined,
        type: type === 'spouse' ? 'Spouse' : 'Children',
        typeValue: type,
        isDeleted: isDeleted,
        deleteRequest: deleteReq,
        isNewFromRequest: member._isNewFromRequest || false,
        addRequest: member._addRequest,
            };
    });

    // Sort members by originalIndex to maintain original order
    // But also ensure: deleted < existing < pending add from request < local new row
    const getRowWeight = (record: any) => {
      if (record.isDeleted) return 0;
      if (record.isNewFromRequest) return 2; // pending add from backend
      if (!record.id) return 3; // local new row (not yet requested)
      return 1; // existing row
    };

    memberData.sort((a: any, b: any) => {
      const wA = getRowWeight(a);
      const wB = getRowWeight(b);
      if (wA !== wB) return wA - wB;
      const idxA = a.originalIndex;
      const idxB = b.originalIndex;
      return idxA - idxB;
    });

    return memberData;
  }, [spouses, children, deletedFamilyMembers, changeRequests]);

  const hasPendingFieldChange = (type: 'spouse' | 'children', index: number) => {
    return changeRequests.some(
      (cr) =>
        cr.status === 'pending' &&
        cr.field_name.startsWith(`family_${type}_${index}_`) &&
        !cr.field_name.endsWith('_add') &&
        !cr.field_name.endsWith('_delete')
    );
  };

  // Get add change requests
  const getAddChangeRequest = (type: 'spouse' | 'children', index: number) => {
    return changeRequests.find(cr => 
      cr.field_name === `family_${type}_${index}_add` && cr.status === 'pending'
    );
  };

  // Family table columns
  const familyColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        
        if (canEdit && !record.isDeleted) {
          return (
            <Select
              value={record.typeValue === 'spouse' ? 'Spouse' : 'Children'}
              onChange={(value) => {
                const newType = value === 'Spouse' ? 'spouse' : 'children';
                handleFamilyTypeChange(record.typeValue, record.originalIndex, newType);
              }}
              style={{ width: '100%' }}
              options={[
                { label: 'Spouse', value: 'Spouse' },
                { label: 'Children', value: 'Children' },
              ]}
            />
          );
        }
        return <span style={cellStyle}>{type}</span>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const nameChangeRequest = getFieldChangeRequest(record.typeValue, record.originalIndex, 'name', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = name || '';
          // Priority: pending change > change request > add request > record value
          if (getFamilyDisplayValue) {
            // Use actualIndex if available (for items in type-specific array), otherwise use originalIndex
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFamilyDisplayValue(record.typeValue, indexToUse, 'name');
            if (pending !== '') {
              displayValue = pending;
            }
          }
          if (nameChangeRequest && nameChangeRequest.new_value) {
            displayValue = nameChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const newMember = JSON.parse(record.addRequest.new_value || '{}');
              displayValue = newMember.name || name || '';
            } catch (e) {
              displayValue = name || '';
            }
          }
          return (
            <Input
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                // Use actualIndex if available (for items in type-specific array), otherwise use originalIndex
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                handleFamilyFieldChange(record.typeValue, indexToUse, 'name', val);
              }}
              placeholder="Enter name"
              style={{ width: '100%' }}
            />
          );
        }
        
        if (nameChangeRequest) {
          if (nameChangeRequest.status === 'approved') {
            return <span>{nameChangeRequest.new_value || name || '-'}</span>;
          }
          if (nameChangeRequest.status === 'rejected') {
            return <span>{nameChangeRequest.old_value || name || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {nameChangeRequest.old_value || name || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {nameChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }
        
        return <span style={cellStyle}>{name || '-'}</span>;
      },
    },
    {
      title: 'Identification No.',
      dataIndex: 'identification_no',
      key: 'identification_no',
      render: (ic: string, record: any) => {
        const cellStyle = record.isDeleted ? { textDecoration: 'line-through', color: '#999' } : {};
        const icChangeRequest = getFieldChangeRequest(record.typeValue, record.originalIndex, 'identification_no', record.id);
        
        if (canEdit && !record.isDeleted) {
          let displayValue = ic || '';
          if (icChangeRequest && icChangeRequest.new_value) {
            displayValue = icChangeRequest.new_value;
          } else if (record.isNewFromRequest && record.addRequest) {
            try {
              const newMember = JSON.parse(record.addRequest.new_value || '{}');
              displayValue = newMember.identification_no || ic || '';
            } catch (e) {
              displayValue = ic || '';
            }
          } else if (getFamilyDisplayValue) {
            // Use actualIndex if available (for items in type-specific array), otherwise use originalIndex
            const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
            const pending = getFamilyDisplayValue(record.typeValue, indexToUse, 'identification_no');
            if (pending !== '') displayValue = pending;
          }
          return (
            <Input
              value={displayValue}
              onChange={(e) => {
                const val = e.target.value;
                // Use actualIndex if available (for items in type-specific array), otherwise use originalIndex
                const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
                handleFamilyFieldChange(record.typeValue, indexToUse, 'identification_no', val);
              }}
              placeholder="Enter identification no"
              style={{ width: '100%' }}
            />
          );
        }
        
        if (icChangeRequest) {
          if (icChangeRequest.status === 'approved') {
            return <span>{icChangeRequest.new_value || ic || '-'}</span>;
          }
          if (icChangeRequest.status === 'rejected') {
            return <span>{icChangeRequest.old_value || ic || '-'}</span>;
          }
          return (
            <div>
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                {icChangeRequest.old_value || ic || '-'}
              </span>
              <span style={{ color: '#ff9800' }}>
                {icChangeRequest.new_value || '-'}
              </span>
            </div>
          );
        }
        
        return <span style={cellStyle}>{ic || '-'}</span>;
      },
    },
    {
      title: 'Attachment',
      key: 'attachment',
      width: 200,
      render: (_: any, record: any) => {
        if (record.isDeleted) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        
        const familyId = record.id;
        const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
        const fileKey = `${record.typeValue}_${indexToUse}`;
        const hasFile = familyFiles[fileKey] && familyFiles[fileKey].length > 0;
        const hasAttachment = record.attachment || (hasFile && familyFiles[fileKey][0].originFileObj);
        
        const attachmentChangeRequest = changeRequests.find(cr =>
          cr.field_name === `family_${record.typeValue}_${record.originalIndex}_attachment` && cr.status === 'pending'
        );
        const addRequestAttachment = record.isNewFromRequest && record.addRequest
          ? (() => {
              try {
                const payload = JSON.parse(record.addRequest.new_value || '{}');
                return payload?.attachment || '';
              } catch {
                return '';
              }
            })()
          : '';
        const hasRequestedAttachment = !!(attachmentChangeRequest?.new_value || addRequestAttachment);

        if (canEdit) {
          return (
            <Space size="small">
              <Upload
                fileList={familyFiles[fileKey] || []}
                showUploadList={false}
                beforeUpload={() => false}
                onChange={(info) => {
                  if (onFamilyFileChange) {
                    const file = info.fileList.length > 0 ? info.fileList[0] : null;
                    onFamilyFileChange(record.typeValue, indexToUse, file);
                  }
                }}
                maxCount={1}
                accept=".update,.png,.jpg,.jpeg"
              >
                <Button size="small" icon={<UploadOutlined />} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} />
              </Upload>
              {hasAttachment && (
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  loading={familyId ? fileActionLoading[familyId]?.view : false}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => {
                    if (familyId) {
                      handleView(familyId);
                    } else {
                      const file = familyFiles[fileKey]?.[0];
                      if (file && file.originFileObj) {
                        const blobUrl = window.URL.createObjectURL(file.originFileObj);
                        window.open(blobUrl, '_blank');
                        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
                      }
                    }
                  }}
                />
              )}
              {hasFile && (
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => {
                    if (onFamilyFileChange) {
                      onFamilyFileChange(record.typeValue, indexToUse, null);
                    }
                  }}
                />
              )}
              {hasRequestedAttachment && (
                <Button
                  size="small"
                  icon={<EyeOutlined style={{ color: '#faad14' }} />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => handleViewRequestedAttachment(record.typeValue, record.originalIndex)}
                />
              )}
            </Space>
          );
        } else {
          // View mode - only show View button if attachment exists
          if (hasAttachment && familyId) {
            return (
              <Space size="small">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  loading={familyId ? fileActionLoading[familyId]?.view : false}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => handleView(familyId)}
                />
                {hasRequestedAttachment && (
                  <Button
                    size="small"
                    icon={<EyeOutlined style={{ color: '#faad14' }} />}
                    style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                    onClick={() => handleViewRequestedAttachment(record.typeValue, record.originalIndex)}
                  />
                )}
              </Space>
            );
          }
          if (hasRequestedAttachment) {
            return (
              <Space size="small">
                <Button
                  size="small"
                  icon={<EyeOutlined style={{ color: '#faad14' }} />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => handleViewRequestedAttachment(record.typeValue, record.originalIndex)}
                />
              </Space>
            );
          }
          return <span>-</span>;
        }
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => {
        // Handle delete request
        if (record.isDeleted && record.deleteRequest) {
          if (!canDelete) return null;
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          } else {
            return null;
          }
        }
        
        // Handle field changes from change requests
        const nameChangeRequest = getFieldChangeRequest(record.typeValue, record.originalIndex, 'name', record.id);
        const icChangeRequest = getFieldChangeRequest(record.typeValue, record.originalIndex, 'identification_no', record.id);
        
        if ((nameChangeRequest || icChangeRequest) && !record.isDeleted) {
          const hasPending = (nameChangeRequest?.status === 'pending') || 
                           (icChangeRequest?.status === 'pending');
          
          if (!canEdit) return null;
          
          if (isCustomerPortal) {
            return hasPending ? <Tag color="orange">Pending</Tag> : null;
          }
          
          return null;
        }
        
        // Handle new member from change request (add request)
        if (record.isNewFromRequest && record.addRequest) {
          if (!canEdit) return null;
          // Customer portal: show pending status
          if (isCustomerPortal) {
            return <Tag color="orange">Pending</Tag>;
          }
          return null;
        }
        
        // Delete button only
        if (canDelete && !record.isDeleted && !record.isNewFromRequest) {
          const indexToUse = record.actualIndex !== undefined ? record.actualIndex : record.originalIndex;
          
          return (
            <Popconfirm
              title="Are you sure you want to delete this family member?"
              onConfirm={() => {
                if (record.typeValue === 'spouse') {
                  removeSpouse(indexToUse);
                } else {
                  removeChild(indexToUse);
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          );
        }
        
        return null;
      },
    },
  ];

  return (
    <div>
      {/* Family Table */}
      <Table
        columns={familyColumns}
        dataSource={processedFamilyMembers}
        pagination={false}
        size="small"
        onRow={(record: any) => {
          const isDeleted = record.isDeleted;
          const isNew = record.isNewFromRequest;
          const isChanged = hasPendingFieldChange(record.typeValue, record.originalIndex);

          // Priority: delete (red) > add (blue) > change (yellow)
          if (isDeleted) {
            return { style: { backgroundColor: '#fff1f0' } };
          }
          if (isNew) {
            return { style: { backgroundColor: '#e6f7ff' } };
          }
          if (isChanged) {
            return { style: { backgroundColor: '#fffbe6' } };
          }
          return {};
        }}
        footer={() => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {canEdit && processedFamilyMembers.filter(m => !m.isDeleted && !m.isNewFromRequest).length < maxFamilyCount && (
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => {
                  if (spouses.length === 0) {
                    addSpouse();
                  } else {
                    addChild();
                  }
                }}
                style={{ color: '#ff4d4f', padding: 0 }}
              >
                (max {maxFamilyCount})
              </Button>
            )}
            {!canEdit && processedFamilyMembers.length === 0 && (
              <span style={{ color: '#999' }}>No family members</span>
            )}
          </div>
        )}
      />
    </div>
  );
};
